import React, { useState, useRef, useEffect } from "react";
import { FiChevronDown } from "react-icons/fi";

export interface DropdownOption {
    label: string;
    value: string | number;
}

const ChevronDown = FiChevronDown as any;

interface SelectDropdownProps {
    label?: string;
    name: string;
    value: string | number;
    onChange: (value: string | number) => void;
    options: DropdownOption[];
    error?: string;
    icon?: React.ReactNode;
    isSearchable?: boolean;
    borderBotttom?: boolean;
    overlay?: boolean;
    custome?: boolean;
}

const CustomDropdown: React.FC<SelectDropdownProps> = ({
    label,
    name,
    value,
    onChange,
    options,
    error,
    icon,
    isSearchable = true,
    borderBotttom,
    overlay,
    custome
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const filteredOptions = options.filter(o =>
        o.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Close dropdown when clicking outside
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    // Auto focus input
    useEffect(() => {
        if (isOpen && isSearchable && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen, isSearchable]);

    const selectedLabel =
        options.find(o => o.value === value)?.label || "Select";

    return (
        <div className="w-full" ref={dropdownRef}>

            {/* {label && <label className="custom_label">{label}</label>} */}

            <div className="relative">
                <div
                    className={`
                    custom-dropdown-control h-12 rounded-xl border border-gray-200 bg-gray-50/50 dark:border-zinc-600 dark:bg-zinc-800 px-3 flex items-center cursor-pointer
                    transition-all duration-200 hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-[#7E57C2]/10
                `}
                    onClick={() => setIsOpen(!isOpen)}
                    tabIndex={0} // Needed for keyboard but we remove outline
                    style={{ outline: "none" }}
                >
                    {/* Left Icon */}
                    {icon && (
                        <span className={`custom-dropdown-icon absolute left-3.5 text-gray-400 ${overlay ? "opacity-80" : ""}`}>
                            {icon}
                        </span>
                    )}
                    {/* Input / Selected */}
                    {isOpen && isSearchable ? (
                        <input
                            ref={inputRef}
                            className={`w-full bg-transparent outline-none border-none text-sm text-gray-700 dark:text-zinc-100
                            ${icon ? "ps-10" : "ps-2"} !py-0`}
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                outline: 'none',
                                boxShadow: 'none',
                                color: 'inherit',
                            }}
                        />
                    ) : (
                        <span
                            className={`text-sm font-medium text-gray-700 dark:text-zinc-100 ${icon ? "ps-8" : ""}`}
                        >
                            {selectedLabel}
                        </span>
                    )}

                    {/* Right Icon */}
                    <ChevronDown
                        className={`absolute right-3 transition-transform duration-200 pointer-events-none 
                        ${isOpen ? "rotate-180" : ""}`}
                        size={18}
                        color="#777"
                    />
                </div>

                {/* Dropdown menu */}
                {isOpen && (
                    <ul className="custom-dropdown-menu dark-popup theme-scrollbar absolute left-0 z-20 mt-2 max-h-44 w-full overflow-y-auto rounded-xl border border-gray-100 bg-white p-1 shadow-lg dark:border-zinc-600 dark:bg-zinc-800">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((opt) => (
                                <li
                                    key={opt.value}
                                    onClick={() => {
                                        onChange(opt.value);
                                        setIsOpen(false);
                                    }}
                                    className={`custom-dropdown-option cursor-pointer rounded-lg px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-zinc-700
                                    ${opt.value === value ? "custom-dropdown-option-selected bg-purple-50 text-[#7E57C2] font-semibold dark:bg-purple-400/15 dark:text-purple-300" : custome ? "text-gray-700 dark:text-zinc-200" : "text-gray-800 dark:text-zinc-100"}`}
                                >
                                    {opt.label}
                                </li>
                            ))
                        ) : (
                            <li className="px-3 py-2 text-gray-500">No options</li>
                        )}
                    </ul>
                )}
            </div>

            {error && <p className="text-red-500 text-sm ps-10">{error}</p>}
        </div>
    );
};

export default CustomDropdown;
